import React, {
  Suspense,
  lazy,
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef
} from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { applyTheme, readStoredTheme } from './services/theme';

// Páginas
import Login from './pages/Login';
import Signup from './pages/Signup';
import { ForgotPassword, ResetPassword } from './pages/PasswordRecovery';
import IntegrationHubTabs from './components/IntegrationHubTabs';
import NivoraLanding from './pages/NivoraLandingSites';
import DocumentacaoAPI from './pages/DocumentacaoAPI';
import BannersParceiros from './pages/BannersParceiros';
import CheckoutSaas from './pages/CheckoutSaas';
import ParceirosPublico from './pages/ParceirosPublico';
import ParceiroLogin from './pages/ParceiroLogin';
import ParceiroPainel from './pages/ParceiroPainel';
import PlatformOwnerLogin from './pages/PlatformOwnerLogin';
import OwnerPanel from './pages/OwnerPanel';
import { captureAffiliateReferralFromLocation } from './services/partnerApiClient';

import {
  canAccessPath,
  getDefaultPathForUser,
  getPartnerPortalPath,
  getPlatformOwnerPath,
  hashToPath,
  PUBLIC_ROUTES
} from './accessControl';
import {
  applyTenantBranding,
  getCurrentTenantId,
  loadTenantBranding,
  subscribeTenantBranding
} from './services/tenantBranding';
import {
  hasErpSession,
  isCloudAuthoritativeSession,
  logoutErpSession,
  restoreErpSession,
  startErpSessionHeartbeat
} from './services/erpSessionClient';
import {
  markPerformance,
  recordPerformance,
  startPerformanceSpan
} from './services/performanceTelemetry';
import { startCloudRealtime } from './services/cloudRealtimeClient';
import { provisionalCentralUser } from './services/centralSessionUser';
import {
  routeCanRenderWithoutPlatformBridge,
  routeDataTelemetry,
  routeNeedsInitialCentralSync,
  routeNeedsPlatformBridge
} from './routeDataPolicy';

// Componentes
import Sidebar from './components/Sidebar';
import Navbar from './components/Navbar';
import FirstAccessTutorial from './components/FirstAccessTutorial';
import SessionReplacedNotice from './components/SessionReplacedNotice';
import SyncStatusNotice from './components/SyncStatusNotice';
import { installFormControlIdentifierGuard } from './services/formControlIdentifiers';
// Keep the authenticated theme contract after every eager shell stylesheet.
// Lazy route bundles may still arrive later, so the contract uses narrowly
// scoped semantic overrides for their structural surfaces.
import './styles/NivoraThemeOverrides.css';

// As rotas autenticadas deixam de entrar no bundle inicial. O shell continua
// leve e cada rota baixa apenas o módulo que o operador realmente abriu.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const InteligenciaPublico = lazy(() => import('./pages/InteligenciaPublico'));
const PDV = lazy(() => import('./pages/PDV'));
const CaixaOperacional = lazy(() => import('./pages/CaixaOperacional'));
const ComandasMobile = lazy(() => import('./pages/ComandasMobile'));
const GestaoEntregas = lazy(() => import('./pages/GestaoEntregas'));
const Pedidos = lazy(() => import('./pages/Pedidos'));
const Clientes = lazy(() => import('./pages/Clientes'));
const Fornecedores = lazy(() => import('./pages/Fornecedores'));
const Entregadores = lazy(() => import('./pages/Entregadores'));
const Produtos = lazy(() => import('./pages/Produtos'));
const TabelasPreco = lazy(() => import('./pages/TabelasPreco'));
const InventarioOperacional = lazy(() => import('./pages/InventarioOperacional'));
const CatalogoOperacional = lazy(() => import('./pages/CatalogoOperacional'));
const CategoriasProdutos = lazy(() => import('./pages/CategoriasProdutos'));
const TabelaFrete = lazy(() => import('./pages/TabelaFrete'));
const Mesas = lazy(() => import('./pages/Mesas'));
const OrdensCompra = lazy(() => import('./pages/OrdensCompra'));
const Financeiro = lazy(() => import('./pages/Financeiro'));
const FinanceiroOrganizador = lazy(() => import('./pages/FinanceiroOrganizador'));
const DRE = lazy(() => import('./pages/DRE'));
const FinanceiroConfiguracoes = lazy(() => import('./pages/FinanceiroConfiguracoes'));
const Crediario = lazy(() => import('./pages/Crediario'));
const Colaboradores = lazy(() => import('./pages/Colaboradores'));
const Ponto = lazy(() => import('./pages/Ponto'));
const AgendaComissoes = lazy(() => import('./pages/AgendaComissoes'));
const BaseConhecimento = lazy(() => import('./pages/BaseConhecimento'));
const PontoKiosk = lazy(() => import('./pages/PontoKiosk'));
const Dispositivos = lazy(() => import('./pages/Dispositivos'));
const Relatorios = lazy(() => import('./pages/Relatorios'));
const ConsultasDinamicas = lazy(() => import('./pages/ConsultasDinamicas'));
const ConfiguracoesGerais = lazy(() => import('./pages/ConfiguracoesGerais'));
const FAQSuporte = lazy(() => import('./pages/FAQSuporte'));
const ConectaClube = lazy(() => import('./pages/ConectaClube'));
const ConectaAuthorization = lazy(() => import('./pages/ConectaAuthorization'));
const WbuyIntegracao = lazy(() => import('./pages/WbuyIntegracao'));
const CorreiosIntegracao = lazy(() => import('./pages/CorreiosIntegracao'));
const PagarmeIntegracao = lazy(() => import('./pages/PagarmeIntegracao'));
const FiscalPlugNotas = lazy(() => import('./pages/FiscalPlugNotas'));
const EspacoContador = lazy(() => import('./pages/EspacoContador'));
const InstaladorLocal = lazy(() => import('./pages/InstaladorLocal'));

const AUTH_ROUTE_VISUAL_MODES = {
  '/': 'native-canvas',
  '/relatorios': 'native-canvas',
  '/pagarme': 'native-canvas',
  '/pdv': 'transactional',
  '/comandas-mobile': 'transactional',
  '/ponto-kiosk': 'transactional',
  '/autorizar-conecta': 'focused'
};

function getAuthenticatedRouteClassName(path) {
  const route = String(path || '/');
  const routeSlug = route === '/'
    ? 'dashboard'
    : route.replace(/^\/+/, '').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
  const visualMode = AUTH_ROUTE_VISUAL_MODES[route] || 'standard';

  return [
    'nivora-auth-route',
    `nivora-auth-route--${visualMode}`,
    `nivora-auth-route--${routeSlug || 'dashboard'}`
  ].join(' ');
}

function RouteLoading() {
  return (
    <div className="loading route-loading" role="status" aria-live="polite">
      Carregando módulo...
    </div>
  );
}

function PlatformBootstrapState({ error = null }) {
  return (
    <div className="loading app-bootstrap-state" role={error ? 'alert' : 'status'} aria-live="polite">
      {error
        ? 'Nao foi possivel preparar os dados operacionais. Recarregue a pagina para tentar novamente.'
        : 'Preparando os dados operacionais...'}
    </div>
  );
}

const IMMEDIATE_PLATFORM_READY = Promise.resolve();

export function canRenderBeforePlatformReady(user, path, isElectronRuntime = false) {
  return routeCanRenderWithoutPlatformBridge(user, path, isElectronRuntime);
}

export function resolveAppLocation(pathname, hash, fallbackHash = '#/') {
  const entryPath = String(pathname || '/').replace(/\/+$/, '') || '/';
  const locationHash = String(hash || '');
  const hasDeepHash = Boolean(locationHash && locationHash !== '#/');
  // `/login#/` é a raiz autenticada do HashRouter. Tratá-la como a rota HTTP
  // `/login` reinicia indevidamente o bootstrap local e bloqueia o Dashboard.
  const hasPrivateRootHash = entryPath === '/login' && locationHash === '#/';
  const currentPath = (!hasDeepHash && !hasPrivateRootHash && entryPath !== '/')
    ? entryPath
    : hashToPath(locationHash || fallbackHash || '#/');
  return { entryPath, hasDeepHash, currentPath };
}

function App({
  platformReadyPromise = IMMEDIATE_PLATFORM_READY,
  startPlatformBridge = () => platformReadyPromise
}) {
  useLayoutEffect(() => {
    // Public entry points always use the neutral login presentation. The
    // operator preference is restored by MerchantApp only after a session is
    // available.
    applyTheme('light');
  }, []);
  useLayoutEffect(() => installFormControlIdentifierGuard(document), []);
  useEffect(() => {
    void captureAffiliateReferralFromLocation(window.location).catch(() => {
      // Tracking comercial nunca pode impedir a navegacao ou o cadastro.
    });
  }, []);

  const platformOwnerPath = getPlatformOwnerPath(window.location);
  if (platformOwnerPath === '/owner/login') return <PlatformOwnerLogin />;
  if (platformOwnerPath === '/owner') return <OwnerPanel />;

  const partnerPath = getPartnerPortalPath(window.location);
  if (partnerPath === '/parceiros') return <ParceirosPublico />;
  if (partnerPath === '/parceiros/login') return <ParceiroLogin />;
  if (partnerPath === '/parceiros/painel') return <ParceiroPainel />;
  return (
    <MerchantApp
      platformReadyPromise={platformReadyPromise}
      startPlatformBridge={startPlatformBridge}
    />
  );
}

function MerchantApp({ platformReadyPromise, startPlatformBridge }) {
  const firstUsableRecorded = useRef(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useLayoutEffect(() => {
    applyTheme(user ? readStoredTheme() : 'light');
  }, [user]);
  const [platformReady, setPlatformReady] = useState(false);
  const [platformError, setPlatformError] = useState(null);
  const [currentHash, setCurrentHash] = useState(window.location.hash || '#/');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [branding, setBranding] = useState(null);
  const [operationalGate, setOperationalGate] = useState({
    actorKey: '',
    ready: false,
    error: null,
    status: null
  });
  const {
    entryPath,
    hasDeepHash,
    currentPath
  } = resolveAppLocation(window.location.pathname, window.location.hash, currentHash);
  // O site publicado usa o servidor HTTP para caminhos públicos profundos,
  // enquanto o app desktop continua usando HashRouter. Sem esta distinção,
  // /checkout e /criar-conta eram interpretados como / e o guard redirecionava
  // visitantes sem sessão para /login.
  const isErpWebsite = window.electron?.runtime !== 'electron'
    && ['nivoraerp.com.br', 'www.nivoraerp.com.br'].includes(
      String(window.location.hostname || '').toLowerCase()
    );
  const isPublicWebsiteHome = isErpWebsite
    && entryPath === '/'
    && (!window.location.hash || window.location.hash === '#/' || currentPath === '/venda');
  const isPublicWebsiteHelp = isErpWebsite
    && entryPath === '/faq-suporte'
    && !hasDeepHash;
  const isPublicWebsiteApiDocs = isErpWebsite
    && entryPath === '/documentacao-api'
    && !hasDeepHash;
  const isPublicWebsiteContent = isPublicWebsiteHome
    || isPublicWebsiteHelp
    || isPublicWebsiteApiDocs;
  const isPrivateWebsiteEntry = isErpWebsite && entryPath === '/login';
  const isElectronRuntime = window.electron?.runtime === 'electron';
  const needsInitialCentralSync = routeNeedsInitialCentralSync(
    user,
    currentPath,
    isElectronRuntime
  );
  const operationalActorKey = user?.local_actor_ready === true
    ? `${String(user.central_tenant_id || '')}:${String(user.central_user_id || '')}`
    : '';

  useEffect(() => {
    if (!user) return;
    markPerformance('route_data_start', { route: currentPath });
    const routeAuthority = routeDataTelemetry(user, currentPath);
    if (routeAuthority) recordPerformance('route-data-authority', routeAuthority);
    markPerformance('route_data_end', { route: currentPath, status: routeAuthority ? 'ok' : 'unknown' });
  }, [currentPath, user]);

  useEffect(() => {
    if (loading || !user || firstUsableRecorded.current) return;
    if (isElectronRuntime && !platformReady) return;
    if (needsInitialCentralSync && !(
      operationalGate.actorKey === operationalActorKey && operationalGate.ready
    )) return;
    firstUsableRecorded.current = true;
    markPerformance('first_route_render', { route: currentPath });
    markPerformance('first_usable', { route: currentPath });
  }, [currentPath, isElectronRuntime, loading, needsInitialCentralSync, operationalActorKey, operationalGate, platformReady, user]);

  useEffect(() => {
    let active = true;
    Promise.resolve(platformReadyPromise)
      .then(() => {
        if (active) setPlatformReady(true);
      })
      .catch((error) => {
        if (active) setPlatformError(error);
      });
    return () => { active = false; };
  }, [platformReadyPromise]);

  useEffect(() => {
    const needsOperationalBridge = routeNeedsPlatformBridge(
      user,
      currentPath,
      isElectronRuntime
    );
    if (!needsOperationalBridge) return;
    void Promise.resolve(startPlatformBridge()).catch(() => {
      // O estado de erro é propagado pela platformReadyPromise.
    });
  }, [currentPath, isElectronRuntime, startPlatformBridge, user]);

  const handleCentralSessionReplaced = useCallback(() => {
    // Remove somente o cache visual da identidade. Dados operacionais locais
    // e demais preferencias do tenant permanecem intactos.
    setUser(null);
    setMobileNavOpen(false);
    localStorage.removeItem('user');
    void window.electron?.auth?.logout?.();
  }, []);

  const handleCentralSessionReplacementAcknowledged = useCallback(() => {
    if (isErpWebsite) {
      window.location.assign('/login');
    } else {
      window.location.hash = '/login';
    }
  }, [isErpWebsite]);

  const withSessionNotice = (content) => (
    <>
      {content}
      <SessionReplacedNotice
        key="central-session-replaced-notice"
        onSessionReplaced={handleCentralSessionReplaced}
        onAcknowledge={handleCentralSessionReplacementAcknowledged}
      />
    </>
  );

  useEffect(() => {
    // A identidade visual antiga nunca autentica. No desktop a sessao valida
    // vive no processo principal; no navegador ela e restaurada pelo cookie
    // HttpOnly, CSRF e device vinculados ao servidor central.
    if (isPublicWebsiteContent) {
      setLoading(false);
      return undefined;
    }
    // No navegador, a sessao central depende apenas do cookie/token e pode
    // ser restaurada em paralelo ao SQL.js. No Electron, mantemos a espera
    // pelo bridge local porque a sessao vive no processo principal.
    const isBrowserRuntime = !window.electron?.runtime
      || window.electron?.runtime === 'browser';
    if (!platformReady && !isBrowserRuntime) return undefined;
    markPerformance('auth_restore_start');
    const finishSessionRestore = startPerformanceSpan('erp-session-restore');
    let active = true;
    (async () => {
      try {
        localStorage.removeItem('user');
        if (window.electron?.runtime === 'electron' && typeof window.electron.auth?.getSession === 'function') {
          const sessionResult = await window.electron.auth.getSession();
          const sessionUser = sessionResult?.ok ? sessionResult.user : null;
          if (active) setUser(sessionUser);
          return;
        }

        const actor = await restoreErpSession();
        if (!actor) {
          if (active) setUser(null);
          return;
        }
        if (!isCloudAuthoritativeSession(actor)) {
          await logoutErpSession().catch(() => {});
          if (active) setUser(null);
          return;
        }
        const adopted = await window.electron?.auth?.adoptCentralActor?.(actor);
        if (active) {
          // O bridge ainda pode estar inicializando. Usa uma identidade
          // provisoria apenas para montar o shell; assim que o bridge fica
          // pronto, este efeito roda novamente e troca pela identidade local
          // completa, que habilita o banco e a sincronizacao.
          setUser(adopted?.ok
            ? adopted.user
            : (!platformReady ? provisionalCentralUser(actor) : null));
        }
      } catch (error) {
        console.error('Nao foi possivel restaurar a sessao central:', error);
        localStorage.removeItem('user');
        if (active) setUser(null);
      } finally {
        if (active) setLoading(false);
        finishSessionRestore({ status: active ? 'ok' : 'cancelled' });
        markPerformance('auth_restore_end', { status: active ? 'ok' : 'cancelled' });
      }
    })();
    return () => { active = false; };
  }, [isPublicWebsiteContent, platformReady]);

  useEffect(() => {
    if (!user || user.session_mode !== 'central' || window.electron?.runtime === 'electron') return undefined;
    return startErpSessionHeartbeat({
      onError: (error) => {
        if (error?.code !== 'network_unavailable') {
          console.error('Falha no heartbeat da sessao central:', error);
        }
      }
    });
  }, [user]);

  useEffect(() => {
    if (
      !user
      || user.session_mode !== 'central'
      || window.electron?.runtime === 'electron'
      || !isCloudAuthoritativeSession(user)
    ) {
      return undefined;
    }
    return startCloudRealtime(user, {
      onStatus: (status) => {
        recordPerformance('cloud-realtime-status', status);
      }
    });
  }, [user]);

  useEffect(() => {
    const subscribe = window.electron?.auth?.onCentralConnectivityChanged;
    if (window.electron?.runtime !== 'electron' || typeof subscribe !== 'function') return undefined;
    return subscribe((status) => {
      const sessionMode = String(status?.sessionMode || '');
      if (!['central', 'offline_signed'].includes(sessionMode)) return;
      setUser((current) => current ? { ...current, session_mode: sessionMode } : current);
    });
  }, []);

  useEffect(() => {
    if (!user || user.session_mode !== 'central' || window.electron?.runtime === 'electron'
        || !needsInitialCentralSync
        || !platformReady || user.local_actor_ready !== true || !operationalActorKey) {
      return undefined;
    }
    const sync = window.electron?.sync;
    if (typeof sync?.start !== 'function') {
      setOperationalGate({
        actorKey: operationalActorKey,
        ready: false,
        error: new Error('A sincronizacao operacional nao esta disponivel.'),
        status: null
      });
      return undefined;
    }

    let active = true;
    setOperationalGate((current) => (
      current.actorKey === operationalActorKey && current.ready
        ? current
        : { actorKey: operationalActorKey, ready: false, error: null, status: null }
    ));
    const unsubscribe = typeof sync.onStatus === 'function'
      ? sync.onStatus((status = {}) => {
        if (!active) return;
        setOperationalGate((current) => (
          current.actorKey === operationalActorKey
            ? { ...current, status }
            : current
        ));
      })
      : null;

    markPerformance('initial_sync_start');
    void Promise.resolve(sync.start())
      .then((status) => {
        if (!active) return;
        markPerformance('initial_sync_end', { status: 'ok' });
        setOperationalGate({
          actorKey: operationalActorKey,
          ready: true,
          error: null,
          status: status || null
        });
      })
      .catch((error) => {
        console.error('Nao foi possivel iniciar a sincronizacao operacional:', error);
        markPerformance('initial_sync_end', {
          status: 'error',
          errorCode: String(error?.code || error?.status || 'sync_failed')
        });
        if (!active) return;
        setOperationalGate({
          actorKey: operationalActorKey,
          ready: false,
          error,
          status: null
        });
      });

    return () => {
      active = false;
      if (typeof unsubscribe === 'function') unsubscribe();
      void sync.stop?.();
    };
  }, [needsInitialCentralSync, operationalActorKey, platformReady, user]);

  useEffect(() => {
    let active = true;
    markPerformance('tenant_resolution_start');
    const tenantId = getCurrentTenantId(user);
    markPerformance('tenant_resolution_end', { status: tenantId ? 'ok' : 'unknown' });
    const isBrowser = !window.electron?.runtime || window.electron?.runtime === 'browser';
    const canLoadCentralBranding = Boolean(
      user
      && user.session_mode === 'central'
      && hasErpSession()
    );
    const receiveBranding = (nextBranding) => {
      if (!active || !nextBranding || nextBranding.tenantId !== tenantId) return;
      applyTenantBranding(nextBranding);
      setBranding(nextBranding);
    };

    // A identidade visual e complementar. Nunca deve abrir uma requisicao
    // autenticada antes da sessao central estar pronta, nem atrasar a entrada
    // do operador no ERP. O tenant passa a substituir este padrao assim que a
    // sessao for restaurada.
    if (isBrowser && !canLoadCentralBranding) {
      applyTenantBranding({ primaryColor: 'azul' });
      setBranding(null);
      return () => { active = false; };
    }

    const unsubscribe = subscribeTenantBranding(receiveBranding);

    loadTenantBranding(tenantId)
      .then(receiveBranding)
      .catch((error) => {
        // Expiracao/renovacao de sessao nao pode poluir o console nem impedir
        // o uso do ERP; o esquema padrao continua aplicado.
        if (error?.status === 401 || ['invalid_session', 'session_not_recoverable'].includes(error?.code)) return;
        console.error('Erro ao carregar identidade visual:', error);
      });

    return () => {
      active = false;
      unsubscribe();
    };
  }, [user]);

  useEffect(() => {
    const handleHashChange = () => {
      setCurrentHash(window.location.hash || '#/');
      setMobileNavOpen(false);
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setMobileNavOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [mobileNavOpen]);

  useEffect(() => {
    if (loading) return;
    if (isPublicWebsiteContent || (!user && isPrivateWebsiteEntry)) return;
    if (!user && !PUBLIC_ROUTES.includes(currentPath) && currentPath !== '/login') {
      if (isErpWebsite) {
        window.location.replace('/login');
      } else {
        window.location.hash = '/login';
      }
      return;
    }
    if (user && currentPath === '/login') {
      const defaultPath = getDefaultPathForUser(user);
      if (isErpWebsite) {
        window.location.replace(`/login#${defaultPath}`);
      } else {
        window.location.hash = defaultPath;
      }
    }
  }, [currentPath, isErpWebsite, isPrivateWebsiteEntry, isPublicWebsiteContent, loading, user]);

  const handleLogin = (userData) => {
    setUser(userData);
    localStorage.removeItem('user');
    const authorizationHash = currentPath === '/autorizar-conecta'
      ? String(window.location.hash || '')
      : '';
    const defaultPath = getDefaultPathForUser(userData);
    if (isErpWebsite) {
      window.location.replace(authorizationHash ? `/login${authorizationHash}` : `/login#${defaultPath}`);
    } else {
      window.location.hash = authorizationHash || defaultPath;
    }
  };

  const handleLogout = async () => {
    markPerformance('logout');
    try {
      await window.electron?.devices?.disconnectBluetoothReader?.();
    } catch (error) {
      console.error('Nao foi possivel desconectar o leitor Bluetooth durante a saida:', error);
    }
    try {
      if (window.electron?.runtime !== 'electron') await logoutErpSession();
      await window.electron?.auth?.logout?.();
    } catch (error) {
      console.error('Nao foi possivel encerrar a sessao no processo local:', error);
    }
    setUser(null);
    localStorage.removeItem('user');
    if (isErpWebsite) {
      window.location.assign('/login');
    } else {
      window.location.hash = '/login';
    }
  };

  if (isElectronRuntime && !platformReady && !isPublicWebsiteContent && !user) {
    if (platformError) {
      return withSessionNotice(
        <div className="loading" role="alert">
          Não foi possível preparar o ambiente do ERP. Recarregue a página para tentar novamente.
        </div>
      );
    }
    return withSessionNotice(
      <div className="loading" role="status" aria-live="polite">
        Preparando o ambiente do ERP...
      </div>
    );
  }

  if (loading) {
    return withSessionNotice(<div className="loading">Carregando...</div>);
  }

  if (isPublicWebsiteHome) {
    return withSessionNotice(<NivoraLanding />);
  }

  if (isPublicWebsiteHelp) {
    return withSessionNotice(<NivoraLanding initialSection="duvidas" />);
  }

  if (isPublicWebsiteApiDocs) {
    return withSessionNotice(<DocumentacaoAPI />);
  }

  if (currentPath === '/venda') {
    return withSessionNotice(<NivoraLanding />);
  }

  if (currentPath === '/checkout') {
    return withSessionNotice(<CheckoutSaas />);
  }

  if (currentPath === '/criar-conta') {
    return withSessionNotice(<Signup />);
  }

  if (currentPath === '/esqueci-senha') {
    return withSessionNotice(<ForgotPassword />);
  }

  if (currentPath === '/redefinir-senha') {
    return withSessionNotice(<ResetPassword />);
  }

  if (!user) {
    return withSessionNotice(<Login onLogin={handleLogin} />);
  }

  // Dashboard, Inteligência e catálogos centrais usam o PostgreSQL e não devem
  // esperar a réplica SQL.js terminar de abrir. A ponte local passa a ser
  // carregada sob demanda apenas quando uma mutação ainda depende dela.
  const cloudFirstRoute = canRenderBeforePlatformReady(user, currentPath, isElectronRuntime);
  const operationalDataReady = !needsInitialCentralSync || (
    operationalGate.actorKey === operationalActorKey
    && operationalGate.ready
  );
  const routeBootstrapError = platformError || (
    needsInitialCentralSync
    && operationalGate.actorKey === operationalActorKey
    && operationalGate.error
  );
  const platformBlocksRoute = (
    (!platformReady || platformError) && !cloudFirstRoute
  ) || !operationalDataReady;

  return withSessionNotice(
    <Router>
      <div className="app-container">
        <Navbar
          user={user}
          branding={branding}
          onLogout={handleLogout}
          menuOpen={mobileNavOpen}
          onMenuToggle={() => setMobileNavOpen((open) => !open)}
        />
        {user.session_mode === 'offline_signed' && (
          <div className="offline-contingency-banner" role="status">
            Modo offline seguro: continue trabalhando neste computador. Os dados serão sincronizados quando a conexão voltar.
          </div>
        )}
        <SyncStatusNotice user={user} />
        <div className="app-body">
          <Sidebar
            user={user}
            open={mobileNavOpen}
            onNavigate={() => setMobileNavOpen(false)}
          />
          <button
            type="button"
            className={`sidebar-scrim ${mobileNavOpen ? 'is-visible' : ''}`}
            aria-label="Fechar menu de navegacao"
            onClick={() => setMobileNavOpen(false)}
          />
          <main className="main-content">
            {platformBlocksRoute ? (
              <PlatformBootstrapState error={routeBootstrapError} />
            ) : (
              <Suspense fallback={<RouteLoading />}>
              <Routes>
              <Route path="/venda" element={<NivoraLanding />} />
              <Route path="/checkout" element={<CheckoutSaas />} />
              <Route path="/" element={<ProtectedRoute user={user} path="/"><Dashboard user={user} /></ProtectedRoute>} />
              <Route path="/inteligencia-publico" element={<ProtectedRoute user={user} path="/inteligencia-publico"><InteligenciaPublico user={user} /></ProtectedRoute>} />
              <Route path="/pdv" element={<ProtectedRoute user={user} path="/pdv"><PDV /></ProtectedRoute>} />
              <Route path="/caixa" element={<ProtectedRoute user={user} path="/caixa"><CaixaOperacional /></ProtectedRoute>} />
              <Route path="/comandas-mobile" element={<ProtectedRoute user={user} path="/comandas-mobile"><ComandasMobile /></ProtectedRoute>} />
              <Route path="/entregas" element={<ProtectedRoute user={user} path="/entregas"><GestaoEntregas /></ProtectedRoute>} />
              <Route path="/pedidos" element={<ProtectedRoute user={user} path="/pedidos"><Pedidos /></ProtectedRoute>} />
              <Route path="/clientes" element={<ProtectedRoute user={user} path="/clientes"><Clientes /></ProtectedRoute>} />
              <Route path="/fornecedores" element={<ProtectedRoute user={user} path="/fornecedores"><Fornecedores /></ProtectedRoute>} />
              <Route path="/entregadores" element={<ProtectedRoute user={user} path="/entregadores"><Entregadores /></ProtectedRoute>} />
              <Route path="/produtos" element={<ProtectedRoute user={user} path="/produtos"><Produtos /></ProtectedRoute>} />
              <Route path="/tabelas-preco" element={<ProtectedRoute user={user} path="/tabelas-preco"><TabelasPreco /></ProtectedRoute>} />
              <Route path="/inventario" element={<ProtectedRoute user={user} path="/inventario"><InventarioOperacional /></ProtectedRoute>} />
              <Route path="/catalogo-operacional" element={<ProtectedRoute user={user} path="/catalogo-operacional"><CatalogoOperacional /></ProtectedRoute>} />
              <Route path="/categorias-produtos" element={<ProtectedRoute user={user} path="/categorias-produtos"><CategoriasProdutos /></ProtectedRoute>} />
              <Route path="/tabela-frete" element={<ProtectedRoute user={user} path="/tabela-frete"><TabelaFrete /></ProtectedRoute>} />
              <Route path="/mesas" element={<ProtectedRoute user={user} path="/mesas"><Mesas /></ProtectedRoute>} />
              <Route path="/ordens-compra" element={<ProtectedRoute user={user} path="/ordens-compra"><OrdensCompra /></ProtectedRoute>} />
              <Route path="/financeiro" element={<ProtectedRoute user={user} path="/financeiro"><Financeiro /></ProtectedRoute>} />
              <Route path="/financeiro-organizador" element={<ProtectedRoute user={user} path="/financeiro-organizador"><FinanceiroOrganizador /></ProtectedRoute>} />
              <Route path="/dre" element={<ProtectedRoute user={user} path="/dre"><DRE /></ProtectedRoute>} />
              <Route path="/financeiro-configuracoes" element={<ProtectedRoute user={user} path="/financeiro-configuracoes"><FinanceiroConfiguracoes /></ProtectedRoute>} />
              <Route path="/crediario" element={<ProtectedRoute user={user} path="/crediario"><Crediario /></ProtectedRoute>} />
              <Route path="/colaboradores" element={<ProtectedRoute user={user} path="/colaboradores"><Colaboradores /></ProtectedRoute>} />
              <Route path="/ponto" element={<ProtectedRoute user={user} path="/ponto"><Ponto /></ProtectedRoute>} />
              <Route path="/agenda-comissoes" element={<ProtectedRoute user={user} path="/agenda-comissoes"><AgendaComissoes /></ProtectedRoute>} />
              <Route path="/ponto-kiosk" element={<ProtectedRoute user={user} path="/ponto-kiosk"><PontoKiosk /></ProtectedRoute>} />
              <Route path="/base-conhecimento" element={<ProtectedRoute user={user} path="/base-conhecimento"><BaseConhecimento /></ProtectedRoute>} />
              <Route path="/relatorios" element={<ProtectedRoute user={user} path="/relatorios"><Relatorios /></ProtectedRoute>} />
              <Route path="/consultas" element={<ProtectedRoute user={user} path="/consultas"><ConsultasDinamicas /></ProtectedRoute>} />
              <Route path="/faq-suporte" element={<ProtectedRoute user={user} path="/faq-suporte"><FAQSuporte /></ProtectedRoute>} />
              <Route path="/configuracoes" element={<ProtectedRoute user={user} path="/configuracoes"><ConfiguracoesGerais /></ProtectedRoute>} />
              <Route path="/fiscal" element={<ProtectedRoute user={user} path="/fiscal"><FiscalPlugNotas /></ProtectedRoute>} />
              <Route path="/espaco-contador" element={<ProtectedRoute user={user} path="/espaco-contador"><EspacoContador user={user} /></ProtectedRoute>} />
              <Route path="/nfe-mg" element={<ProtectedRoute user={user} path="/nfe-mg"><FiscalPlugNotas /></ProtectedRoute>} />
              <Route path="/nfse-itabira" element={<ProtectedRoute user={user} path="/nfse-itabira"><FiscalPlugNotas /></ProtectedRoute>} />
              <Route path="/dispositivos" element={<ProtectedRoute user={user} path="/dispositivos"><><IntegrationHubTabs /><Dispositivos /></></ProtectedRoute>} />
              <Route path="/conecta-clube" element={<ProtectedRoute user={user} path="/conecta-clube"><ConectaClube /></ProtectedRoute>} />
              <Route path="/autorizar-conecta" element={<ProtectedRoute user={user} path="/autorizar-conecta"><ConectaAuthorization user={user} /></ProtectedRoute>} />
              <Route path="/wbuy" element={<ProtectedRoute user={user} path="/wbuy"><WbuyIntegracao /></ProtectedRoute>} />
              <Route path="/correios" element={<ProtectedRoute user={user} path="/correios"><CorreiosIntegracao /></ProtectedRoute>} />
              <Route path="/pagarme" element={<ProtectedRoute user={user} path="/pagarme"><PagarmeIntegracao user={user} /></ProtectedRoute>} />
              <Route path="/central-novidades" element={<ProtectedRoute user={user} path="/central-novidades"><BannersParceiros /></ProtectedRoute>} />
              <Route path="/banners-parceiros" element={<ProtectedRoute user={user} path="/banners-parceiros"><BannersParceiros /></ProtectedRoute>} />
              <Route path="/instalador-local" element={<ProtectedRoute user={user} path="/instalador-local"><InstaladorLocal /></ProtectedRoute>} />
              <Route path="*" element={<Navigate to="/" />} />
              </Routes>
              </Suspense>
            )}
          </main>
          {platformReady && (
            <FirstAccessTutorial
              user={user}
              onNavigate={(path) => { window.location.hash = path; }}
            />
          )}
        </div>
      </div>
    </Router>
  );
}

function ProtectedRoute({ user, path, children }) {
  const isAllowed = canAccessPath(user, path);
  const dataPolicy = routeDataTelemetry(user, path);

  return (
    <div
      className={getAuthenticatedRouteClassName(path)}
      data-route={path}
      data-route-access={isAllowed ? 'allowed' : 'denied'}
      data-route-mode={dataPolicy?.mode || 'undeclared'}
      data-route-read-authority={dataPolicy?.readAuthority || 'undeclared'}
      data-route-write-authority={dataPolicy?.writeAuthority || 'undeclared'}
    >
      {isAllowed ? children : <AccessDenied user={user} />}
    </div>
  );
}

function AccessDenied({ user }) {
  return (
    <div className="card">
      <div className="card-header"><i className="fas fa-lock"></i> Acesso restrito</div>
      <div className="card-body">
        <p style={{ color: '#5d6d7e', lineHeight: 1.6 }}>
          Seu usuario nao possui permissao para visualizar ou operar esta rotina.
        </p>
        <a className="btn btn-primary" href={`#${getDefaultPathForUser(user)}`}>
          <i className="fas fa-arrow-left"></i> Ir para area permitida
        </a>
      </div>
    </div>
  );
}

export default App;
